import React, { useEffect, useState } from "react";
import { X, Copy, Check, AlertCircle, Loader2, Clock } from "lucide-react";
import { api } from "@/lib/api";

export default function DepositModal({ initialAsset, onClose }) {
  const [assets, setAssets] = useState(null);
  const [asset, setAsset] = useState(initialAsset || null);
  const [network, setNetwork] = useState(null);
  const [addr, setAddr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const [simAmount, setSimAmount] = useState("");
  const [simBusy, setSimBusy] = useState(false);
  const [simMsg, setSimMsg] = useState("");
  const [devMode, setDevMode] = useState(false);

  const [whitelist, setWhitelist] = useState([]);
  const [whitelisting, setWhitelisting] = useState(false);

  const loadWhitelist = async () => {
    try {
      const r = await api.get("/api/wallet/deposit-whitelist");
      setWhitelist(r.data.deposit_whitelist || []);
    } catch (e) {
      console.error("Failed to load deposit whitelist in modal", e);
    }
  };

  useEffect(() => {
    api.get("/api/wallet/assets").then((r) => {
      const real = r.data.assets.filter((a) => !a.is_fiat);
      setAssets(real);
      if (!asset && real.length) {
        setAsset(initialAsset || real[0].symbol);
      }
    });
    loadWhitelist();
    api.get("/api/config").then((r) => setDevMode(Boolean(r.data?.dev_mode))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-pick first network when asset changes
  useEffect(() => {
    if (!asset || !assets) return;
    const a = assets.find((x) => x.symbol === asset);
    if (a && a.networks.length) {
      setNetwork(a.networks[0].network);
    }
  }, [asset, assets]);

  // Fetch address whenever asset+network resolved
  useEffect(() => {
    if (!asset || !network) return;
    setLoading(true);
    setError("");
    setAddr(null);
    api
      .get("/api/wallet/deposit-address", { params: { asset, network } })
      .then((r) => setAddr(r.data))
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [asset, network]);

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const whitelistThisAddress = async () => {
    if (!addr) return;
    setWhitelisting(true);
    try {
      await api.post("/api/wallet/deposit-whitelist", {
        asset,
        network,
        address: addr.address,
        label: `My ${asset} Deposit Address`,
        memo: addr.memo || undefined,
        is_own_address: true,
      });
      await loadWhitelist();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Failed to whitelist address";
      const text = typeof msg === "string" ? msg : JSON.stringify(msg);
      if (text.toLowerCase().includes("kyc")) {
        alert("Complete KYC approval before whitelisting a deposit address.");
      } else {
        alert(text);
      }
    } finally {
      setWhitelisting(false);
    }
  };

  const runSimulateDeposit = async () => {
    if (!simAmount || !asset || !network) return;
    setSimBusy(true);
    setSimMsg("");
    try {
      await api.post("/api/wallet/dev/simulate-deposit", null, {
        params: { asset, network, amount: simAmount },
      });
      setSimMsg("Simulated deposit queued — balance credits after confirmations.");
      setSimAmount("");
    } catch (e) {
      setSimMsg(e.response?.data?.detail || e.message || "Simulation failed");
    } finally {
      setSimBusy(false);
    }
  };

  const getCoolingStatus = (item) => {
    if (item.status === "pending_admin") {
      return { active: false, label: "Pending admin approval" };
    }
    if (item.status === "rejected") {
      return { active: false, label: "Rejected" };
    }
    if (item.is_active) {
      return { active: true, label: "Active" };
    }
    if (item.cooling_until) {
      const until = new Date(item.cooling_until);
      if (until > new Date()) {
        return { active: false, label: `Cooling (${getCoolingHoursLeft(item)}h left)` };
      }
    }
    return { active: false, label: "Awaiting activation" };
  };

  const getCoolingHoursLeft = (item) => {
    if (!item.cooling_until) return 0;
    const until = new Date(item.cooling_until);
    const diffMs = until - new Date();
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60)));
  };

  const aSel = assets?.find((x) => x.symbol === asset);
  const nSel = aSel?.networks.find((n) => n.network === network);

  const whitelistEntry = addr
    ? whitelist.find((w) => w.asset === asset && w.network === network && w.address === addr.address)
    : null;

  return (
    <Backdrop onClose={() => onClose(false)}>
      <Panel>
        <Header title="Deposit Crypto" onClose={() => onClose(false)} testId="deposit-close" />
        <div className="p-6 space-y-5">
          {/* Asset selector */}
          <Field label="Asset">
            <select
              value={asset || ""}
              onChange={(e) => setAsset(e.target.value)}
              className="w-full px-3 py-2.5 rounded-[10px] border border-[var(--cm-border)] bg-white text-[14px] text-cm-navy"
              data-testid="deposit-asset-select"
            >
              {(assets || []).map((a) => (
                <option key={a.symbol} value={a.symbol}>
                  {a.symbol} — {a.name}
                </option>
              ))}
            </select>
          </Field>

          {/* Network selector */}
          {aSel?.networks.length > 0 && (
            <Field label="Network">
              <div className="grid grid-cols-3 gap-2">
                {aSel.networks.map((n) => (
                  <button
                    key={n.network}
                    onClick={() => setNetwork(n.network)}
                    className={`px-3 py-2 rounded-[10px] border text-[13px] font-semibold transition-colors ${
                      network === n.network
                        ? "border-cm-blue bg-blue-50 text-cm-blue"
                        : "border-[var(--cm-border)] text-cm-navy hover:bg-cm-surface"
                    }`}
                    data-testid={`deposit-network-${n.network}`}
                  >
                    {n.network}
                  </button>
                ))}
              </div>
            </Field>
          )}

          {/* Address display */}
          {loading ? (
            <div className="flex items-center gap-2 text-cm-grey text-[13px]">
              <Loader2 size={14} className="animate-spin" /> Generating address…
            </div>
          ) : error ? (
            <div className="flex items-start gap-2 p-3 rounded-[10px] bg-red-50 text-red-700 text-[12px]">
              <AlertCircle size={14} className="mt-0.5" /> {error}
            </div>
          ) : addr ? (
            <>
              <Field label="Deposit Address">
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={addr.address}
                    className="flex-1 px-3 py-2.5 rounded-[10px] border border-[var(--cm-border)] bg-cm-surface text-[12px] font-mono text-cm-navy"
                    data-testid="deposit-address"
                  />
                  <button
                    onClick={() => copy(addr.address)}
                    className="px-3 py-2.5 rounded-[10px] bg-cm-navy text-white text-[12px] font-semibold inline-flex items-center gap-1.5"
                    data-testid="deposit-copy-address"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
              </Field>

              {/* Whitelist Status */}
              <div className="text-[12px] flex items-center justify-between p-3 rounded-[10px] bg-cm-surface/50 border border-[var(--cm-border)]">
                <span className="text-cm-grey font-medium">Whitelist Status:</span>
                {whitelistEntry ? (
                  getCoolingStatus(whitelistEntry).active ? (
                    <span className="text-emerald-600 font-semibold flex items-center gap-1" data-testid="deposit-whitelist-status-active">
                      <Check size={14} /> Whitelisted (Active)
                    </span>
                  ) : (
                    <span className="text-amber-600 font-semibold flex items-center gap-1" data-testid="deposit-whitelist-status-cooling">
                      <Clock size={14} className="animate-pulse" /> {getCoolingStatus(whitelistEntry).label}
                    </span>
                  )
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-amber-600 font-semibold flex items-center gap-1" data-testid="deposit-whitelist-status-none">
                      <AlertCircle size={14} /> Not Whitelisted
                    </span>
                    <button
                      onClick={whitelistThisAddress}
                      disabled={whitelisting}
                      className="px-2.5 py-1 rounded bg-cm-blue text-white text-[11px] font-bold hover:bg-cm-blue-deep active:bg-cm-blue-deep disabled:opacity-50 flex items-center gap-1"
                      data-testid="deposit-whitelist-action"
                    >
                      {whitelisting && <Loader2 size={10} className="animate-spin" />}
                      Whitelist Now
                    </button>
                  </div>
                )}
              </div>

              {/* Info/Warning Banner */}
              {(!whitelistEntry || !getCoolingStatus(whitelistEntry).active) && (
                <div className="p-3 rounded-[10px] bg-amber-50 border border-amber-200 text-amber-800 text-[12px]">
                  ⚠ <b>Held Funds Warning:</b> Deposits to non-whitelisted or cooling addresses will remain in your <b>Held</b> balance until whitelisted.
                </div>
              )}

              {addr.memo && (
                <Field label="Destination Tag / Memo (REQUIRED)">
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={addr.memo}
                      className="flex-1 px-3 py-2.5 rounded-[10px] border border-amber-300 bg-amber-50 text-[13px] font-mono text-cm-navy"
                      data-testid="deposit-memo"
                    />
                    <button
                      onClick={() => copy(addr.memo)}
                      className="px-3 py-2.5 rounded-[10px] bg-cm-navy text-white text-[12px] font-semibold"
                    >
                      Copy
                    </button>
                  </div>
                  <div className="text-[11px] text-amber-700 mt-1.5">
                    You MUST include this tag — deposits without it will be lost.
                  </div>
                </Field>
              )}

              <div className="space-y-1.5 text-[12px] text-cm-grey p-3 rounded-[10px] bg-cm-surface">
                <div>• Send only <b className="text-cm-navy">{asset}</b> to this address on the <b className="text-cm-navy">{network}</b> network.</div>
                <div>• Minimum deposit: <b className="text-cm-navy">{nSel?.min_deposit} {asset}</b></div>
                <div>• Confirmations required: <b className="text-cm-navy">{nSel?.required_confirmations}</b></div>
                <div>• Whitelist this address first — unverified deposits are held until approved.</div>
                <div className="text-amber-700">• Sending other assets or wrong network will result in permanent loss.</div>
              </div>

              {devMode ? (
              <div className="rounded-[10px] border border-dashed border-amber-300 bg-amber-50 p-4 space-y-2">
                <div className="text-[11px] font-bold uppercase tracking-wider text-amber-800">
                  Dev / Demo — Simulate Deposit (Phase B2)
                </div>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder={`Amount in ${asset}`}
                    value={simAmount}
                    onChange={(e) => setSimAmount(e.target.value)}
                    className="flex-1 px-3 py-2 rounded-[8px] border border-amber-300 bg-white text-[13px]"
                    data-testid="deposit-sim-amount"
                  />
                  <button
                    type="button"
                    disabled={simBusy || !simAmount}
                    onClick={runSimulateDeposit}
                    className="px-4 py-2 rounded-[8px] bg-amber-600 text-white text-[12px] font-semibold disabled:opacity-50"
                    data-testid="deposit-sim-submit"
                  >
                    {simBusy ? "Simulating…" : "Simulate"}
                  </button>
                </div>
                {simMsg && <div className="text-[12px] text-amber-800">{simMsg}</div>}
                <div className="text-[11px] text-amber-700">
                  This auto-confirms over {nSel?.required_confirmations} blocks (~{(nSel?.required_confirmations || 6) * 8}s) and credits your balance.
                </div>
              </div>
              ) : null}
            </>
          ) : null}
        </div>

        <Footer>
          <button
            onClick={() => onClose(true)}
            className="px-5 py-2 rounded-[10px] bg-cm-blue text-white text-[13px] font-semibold"
            data-testid="deposit-done"
          >
            Done
          </button>
        </Footer>
      </Panel>
    </Backdrop>
  );
}

// Reusable layout sub-components — kept inline to keep the modal self-contained.
export function Backdrop({ children, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4 animate-in fade-in"
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-[520px] max-h-[92vh] overflow-y-auto">
        {children}
      </div>
    </div>
  );
}
export function Panel({ children }) {
  return <div className="bg-white rounded-[18px] shadow-2xl border border-[var(--cm-border)]">{children}</div>;
}
export function Header({ title, onClose, testId }) {
  return (
    <div className="px-6 py-4 border-b border-[var(--cm-border)] flex items-center justify-between">
      <h3 className="text-[17px] font-bold text-cm-navy">{title}</h3>
      <button
        onClick={onClose}
        className="p-1 rounded hover:bg-cm-surface text-cm-grey"
        data-testid={testId}
        aria-label="Close"
      >
        <X size={18} />
      </button>
    </div>
  );
}
export function Footer({ children }) {
  return <div className="px-6 py-4 border-t border-[var(--cm-border)] flex justify-end gap-2">{children}</div>;
}
export function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[12px] font-semibold text-cm-navy uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}
